import cx from 'classnames';
import {observer} from 'mobx-react';
import {NextPageContext} from 'next';
import React, {useEffect} from 'react';
import {dehydrate, QueryClient} from 'react-query';

import {FOUR_HOURS, RENDER_MODES} from '@udemy/nextjs-core';
import {AlertBanner} from '@udemy/react-messaging-components';
import {Loader} from '@udemy/react-reveal-components';
import {useStores} from '@udemy/store-provider';

import {DevHubPageFeature} from 'src/features/dev-hub';
import {getDevHubLayout} from 'src/layouts/devhub-layout';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {ApiClientStore} from 'src/stores/api-client-store';
import {RootStore} from 'src/stores/root-store';
import {AppContextData} from 'src/types/page-props';

import styles from './api-clients.module.less';
import {getStaticAppContext} from '../api/context.api';
import {AddApiClientBox} from './components/add-api-client-box';
import {ApiClientBox} from './components/api-client-box';

export async function getStaticProps(context: NextPageContext) {
    const client = new QueryClient();

    const locale = context.locale as string;
    const translations = await getCompactTranslations(locale);

    // Resolve appContext
    const [staticData] = await Promise.all([getStaticAppContext({locale})]);

    const appContext: AppContextData = Object.assign({}, staticData);

    return {
        props: {
            appContext,
            client: dehydrate(client),
            pageKey: 'dev_hub',
            locale: context.locale,
            renderMode: RENDER_MODES.ISR,
            translations,
        },
        revalidate: FOUR_HOURS,
    };
}

const ApiClientBoxes = observer(({store}: {store: ApiClientStore}) => {
    return (
        <div data-testid="api-clients-box" className={styles['client-container']}>
            {store.apiClients.map((client, index) => (
                <ApiClientBox
                    key={client.clientId}
                    title={client.clientName}
                    clientId={client.clientId}
                    boxIndex={index}
                />
            ))}
            <AddApiClientBox apiClientStore={store} />
        </div>
    );
});

function ApiClientPage() {
    const [rootStore] = useStores([RootStore]);
    const apiClientStore = rootStore.apiClientStore;

    useEffect(() => {
        apiClientStore.fetchApiClients();
    }, [apiClientStore]);

    return (
        <DevHubPageFeature>
            <div className={styles['api-clients-container']}>
                <h1 className="ud-heading-serif-xxl">API clients</h1>
                <hr />
                <p className={cx('ud-text-md', styles['intro'])}>
                    Creating API clients to start building on the Udemy platform, or integrate with
                    the LMS/LXP. Find your API keys, and manage the scopes of your API clients all
                    from this page.
                </p>
                {/* We don't show anything for idle state yet. We could just show to "add client" button */}
                {apiClientStore.loadingState === 'loaded' && (
                    <ApiClientBoxes store={apiClientStore} />
                )}
                {apiClientStore.loadingState === 'loading' && (
                    <span data-testid="api-client-loader">
                        <Loader size="small" />
                    </span>
                )}
                {apiClientStore.loadingState === 'error' && (
                    <AlertBanner
                        title="We failed to fetch the API clients from the server. Please try again later."
                        udStyle="error"
                        actionButtonProps={false}
                        dismissButtonProps={false}
                        body={
                            <p>
                                If the issue persists, please contact{' '}
                                <a href="mailto:business-support@udemy.com">
                                    Udemy Business Support
                                </a>
                                .
                            </p>
                        }
                    />
                )}
            </div>
        </DevHubPageFeature>
    );
}

ApiClientPage.getLayout = getDevHubLayout;

export default observer(ApiClientPage);
